import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Loader2, Building2, UserSearch, Eye, EyeOff } from "lucide-react";
import Navbar from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { formatApiError } from "../lib/api";
import { GoogleButton, startGoogleAuth, routeForRole } from "./Login";

export function RolePicker({ role, setRole, testidPrefix = "role" }) {
  const { t } = useLanguage();
  const options = [
    { value: "candidate", icon: UserSearch, label: t("auth.aCandidate"), desc: t("auth.candidateDesc") },
    { value: "company", icon: Building2, label: t("auth.aCompany"), desc: t("auth.companyDesc") },
  ];
  return (
    <div className="grid grid-cols-2 gap-3">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          data-testid={`${testidPrefix}-${o.value}`}
          onClick={() => setRole(o.value)}
          className={`flex flex-col items-center gap-2 rounded-md border-2 border-slate-900 p-4 text-center transition-transform duration-200 hover:-translate-y-0.5 ${
            role === o.value ? "bg-sky-100 shadow-[4px_4px_0px_0px_rgba(15,23,42,1)]" : "bg-white"
          }`}
        >
          <o.icon className="h-6 w-6 text-slate-900" strokeWidth={1.75} />
          <span className="text-sm font-bold text-slate-900">{o.label}</span>
          <span className="text-xs text-slate-500">{o.desc}</span>
        </button>
      ))}
    </div>
  );
}

export default function Register() {
  const { t, lang } = useLanguage();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [role, setRole] = useState(params.get("role") === "company" ? "company" : "candidate");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await register({ ...form, role, lang });
      navigate(routeForRole(user.role));
    } catch (err) {
      toast.error(formatApiError(err, t("auth.registerError")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="register-page">
      <Navbar />
      <div className="mx-auto flex max-w-md flex-col px-4 py-16">
        <div className="rounded-md border-2 border-slate-900 bg-white p-8 shadow-[8px_8px_0px_0px_rgba(56,189,248,1)]">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{t("auth.registerTitle")}</h1>
          <p className="mt-1 text-sm text-slate-500">{t("auth.registerSubtitle")}</p>

          <div className="mt-6">
            <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-600">{t("auth.iAm")}</label>
            <RolePicker role={role} setRole={setRole} />
          </div>

          <form onSubmit={handleSubmit} className="mt-5 space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("auth.name")}</label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
                <input
                  type="text" required value={form.name}
                  data-testid="register-name-input"
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-md border-2 border-slate-900 bg-white py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("auth.email")}</label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
                <input
                  type="email" required value={form.email}
                  data-testid="register-email-input"
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-md border-2 border-slate-900 bg-white py-2.5 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-600">{t("auth.password")}</label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" strokeWidth={2} />
                <input
                  type={showPassword ? "text" : "password"} required minLength={6} value={form.password}
                  data-testid="register-password-input"
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full rounded-md border-2 border-slate-900 bg-white py-2.5 pl-10 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-register-password-visibility"
                  aria-label="Afficher le mot de passe"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-900"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" strokeWidth={2} /> : <Eye className="h-4 w-4" strokeWidth={2} />}
                </button>
              </div>
            </div>
            <button
              type="submit" disabled={loading}
              data-testid="register-submit-button"
              className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-slate-900 bg-sky-400 py-2.5 font-heading text-sm font-bold text-slate-900 transition-transform duration-200 hover:-translate-y-0.5 hover:bg-sky-500 hover:shadow-[4px_4px_0px_0px_rgba(15,23,42,1)] disabled:opacity-60"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />} {t("auth.registerBtn")}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <div className="h-px flex-1 bg-slate-300" />
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">{t("auth.orContinueWith")}</span>
            <div className="h-px flex-1 bg-slate-300" />
          </div>

          <GoogleButton onClick={() => startGoogleAuth(role)} label={t("auth.google")} />

          <p className="mt-6 text-center text-sm text-slate-500">
            {t("auth.hasAccount")}{" "}
            <Link to="/login" data-testid="goto-login-link" className="font-bold text-sky-600 hover:underline">
              {t("nav.login")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
